import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText } from "lucide-react";
import Container from "@/components/ui/Container";
import Logo from "@/components/common/Logo";
import ThemeToggle from "@/components/ui/ThemeToggle";
import Footer from "@/components/layout/Footer";
import Assistant from "@/components/assistant/Assistant";

// Public Privacy / Terms pages, reachable from the footer and from the mandatory
// "I agree to the Terms & Privacy" links at signup.
//
// The copy below describes what the product ACTUALLY does — the data the schema
// really stores, the permissions the extension really requests, the providers we
// really call. Nothing here is aspirational; if a practice changes, this file
// changes with it.

// Registered-business details we cannot derive from the code. Deliberately blank
// until the business supplies them: every consumer below renders only when the
// value is non-empty, so an unfilled entry is simply absent rather than invented
// — the same approach as the footer's SOCIAL_LINKS.
const ENTITY = {
  legalName: "",     // registered company name
  address: "",       // registered address
  privacyEmail: "",  // e.g. "privacy@…"
  jurisdiction: "",  // governing law, e.g. "India"
};

// Shown at the top of both documents. Bump whenever the copy changes.
const LAST_UPDATED = "17 August 2026";

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Assembled rather than hard-coded so it stays truthful while ENTITY is unfilled:
// with no published inbox, it points at the Contact link every page already has.
function contactSection(subject) {
  const body = [
    ENTITY.privacyEmail
      ? `If you have questions about ${subject}, write to us at ${ENTITY.privacyEmail}.`
      : `If you have questions about ${subject}, use the Contact link in the footer of any page and your message will reach the right team.`,
  ];
  if (ENTITY.legalName) {
    body.push(
      ENTITY.address
        ? `${ENTITY.legalName}, ${ENTITY.address}.`
        : `${ENTITY.legalName}.`
    );
  }
  return { heading: "Contact us", body };
}

const PRIVACY_SECTIONS = [
  {
    heading: "What this policy covers",
    body: [
      "This policy applies to the AI-QA-Engine website, the web application you sign in to, and the AI-QA-Engine browser extension. It explains what we collect, why we collect it, who we share it with, and the choices you have.",
      "If you use AI-QA-Engine through an employer's account, that account is controlled by your administrator, who can see the projects, runs and reports created inside it.",
    ],
  },
  {
    heading: "Information you give us",
    list: [
      "Account details — your name, email address and password. Passwords are stored only as a hash; we never hold the original.",
      "Company details — company name, billing email, country, and any legal, tax or contact details an administrator chooses to add.",
      "Billing records — your plan, payment status and invoices. Card details are handled by our payment provider and do not reach our servers.",
      "Support messages — anything you send us through the in-app assistant or a support ticket, including the name, email and phone number you supply.",
      "Testing credentials — if you ask a run to sign in to your own application, the username and password you save for it.",
    ],
  },
  {
    heading: "Information created when you use the service",
    list: [
      "Projects and tests — the sites and URLs you point us at, the instructions you write in plain English, and the pages we crawl to understand your application.",
      "Run data — step-by-step logs of each run, its outcome, timings and the model usage it consumed.",
      "Bug reports — the title, description, reproduction steps, expected and actual behaviour, and severity written for a failed run.",
      "Screenshots — the engine captures images of the page it is testing as evidence. A screenshot shows whatever was on screen at that moment, which can include real data inside your own application.",
      "Security and audit records — sign-in times, IP address, API key usage, and an append-only log of significant account actions.",
      "Notifications — in-app messages about your runs, bugs and account.",
    ],
  },
  {
    heading: "How we handle testing credentials",
    body: [
      "Credentials you save so a run can sign in to your application are encrypted before they are stored, and are decrypted only for the moment a run needs them. They are never shown back to you in full, never written into logs or reports, and never visible outside your company's account. You can delete them at any time from the project's settings.",
      "We strongly recommend giving runs a dedicated test account with the least access it needs — never a production administrator account.",
    ],
  },
  {
    heading: "The browser extension",
    body: [
      "The extension requests broad browser permissions because it has to work on whatever site you choose to test. In practice it uses them only for the tab you point it at:",
    ],
    list: [
      "Reading the active tab and running scripts on it — to inspect the page and carry out the steps of your test.",
      "Cookies — to keep the session under test signed in while the run proceeds.",
      "Local storage — to remember your settings and which server the extension talks to.",
    ],
    outro: [
      "The extension does not browse in the background, does not collect your browsing history, and sends data to our servers only to carry out a test you started.",
    ],
  },
  {
    heading: "How we use your information",
    list: [
      "To provide and operate the service, run your tests and produce reports.",
      "To measure usage against your plan and to bill you.",
      "To answer support requests and send service messages about your runs and account.",
      "To keep the service secure — detecting abuse, fraud and unauthorised access.",
      "To fix problems and improve the reliability and quality of the product.",
    ],
    outro: [
      "We do not sell your personal information and we do not use it for advertising.",
    ],
  },
  {
    heading: "AI processing",
    body: [
      "Running a test means sending the instructions you wrote and the content of the page being tested to third-party AI model providers, which plan the steps, decide what to do next, and write the resulting bug report. Only what is needed for that run is sent.",
      "Because the page content is part of what is sent, a test run against a screen containing real customer data will include that data in the request. Prefer a staging environment or test data where you can.",
    ],
  },
  {
    heading: "Who we share it with",
    list: [
      "Service providers who host our infrastructure, deliver email, process payments and provide AI models — each receiving only what they need to perform that function.",
      "Authorities or other parties where we are legally required to disclose.",
      "A successor organisation, if the business is ever merged or acquired, with notice to you.",
    ],
    outro: ["We do not sell personal data to anyone."],
  },
  {
    heading: "Cookies and local storage",
    body: [
      "We do not use advertising or tracking cookies. The application stores a sign-in token and a few preferences — theme, text size and layout density — in your browser so that you stay signed in and the interface looks the way you left it. Signing out clears the token.",
    ],
  },
  {
    heading: "How we protect your data",
    list: [
      "Passwords are hashed, and saved testing credentials are encrypted at rest.",
      "Optional two-factor authentication, with single-use backup codes for recovery.",
      "Each company's data is isolated from every other company's, and access within a company follows the role you were given.",
      "Traffic between your browser and the service is encrypted in transit.",
      "Privileged and administrative actions are recorded in an append-only audit log.",
    ],
    outro: [
      "No system is perfectly secure. If we become aware of a breach affecting your data, we will tell you.",
    ],
  },
  {
    heading: "How long we keep it",
    body: [
      "We keep your data for as long as your account is open. When an account is closed we delete or anonymise the personal data held against it, apart from records we are obliged to retain — invoices and similar financial records, and audit entries — which are kept for the period the applicable law requires.",
      "You can delete individual projects, runs, reports and saved credentials from inside the application at any time.",
    ],
  },
  {
    heading: "Your choices and rights",
    list: [
      "View and update your profile and company details in Settings.",
      "Turn two-factor authentication on or off at any time.",
      "Delete projects, runs, reports and saved testing credentials.",
      "Ask us for a copy of the data we hold about you, or ask us to correct or delete it.",
    ],
    outro: [
      "Depending on where you live, you may also have the right to object to certain processing or to complain to your local data protection authority.",
    ],
  },
  {
    heading: "Children",
    body: [
      "AI-QA-Engine is a tool for businesses. It is not intended for children, and we do not knowingly collect information from anyone under 18.",
    ],
  },
  {
    heading: "Changes to this policy",
    body: [
      "If we change this policy we will update this page and the date shown at the top. Where a change materially affects how we handle your data, we will also announce it in the application.",
    ],
  },
  contactSection("this policy or your data"),
];

const TERMS_SECTIONS = [
  {
    heading: "Agreement to these terms",
    body: [
      "These terms govern your use of AI-QA-Engine. By creating an account or using the service you accept them. If you are accepting on behalf of a company, you confirm that you are authorised to do so and that the company is bound by them.",
      "They should be read together with our Privacy Policy, which explains how we handle data.",
    ],
  },
  {
    heading: "Your account",
    list: [
      "Give accurate details when you sign up and keep them current.",
      "Keep your password and two-factor codes to yourself. You are responsible for activity under your account.",
      "An administrator controls their company's account, may invite or remove team members, and can see the projects, runs and reports created within it.",
      "Tell us promptly if you believe an account has been accessed without permission.",
    ],
  },
  {
    heading: "Plans, runs and billing",
    body: [
      "The service is sold in plans, each including a number of test runs for the billing period. The plans and prices in force are shown on the pricing page and confirmed at checkout; prices can vary by country and exclude taxes unless stated otherwise.",
      "Paid plans are billed in advance for the period shown at checkout. Where a plan renews automatically, cancelling stops the next renewal and your access continues to the end of the period you have already paid for.",
      "The free trial is available once per company.",
    ],
  },
  {
    heading: "Authorised testing only",
    body: [
      "You may only run tests against a website or application that you own, or that you have clear permission from the owner to test. Automated testing of a system you are not authorised to test may be unlawful.",
      "You are responsible for the targets you point the service at, for the credentials you supply, and for the effect a run has on those systems. Do not use the service to attack, overload, scan, or gain access to anything you do not control.",
    ],
  },
  {
    heading: "Acceptable use",
    list: [
      "Do not resell, sublicense or provide access to the service to anyone outside your account.",
      "Do not attempt to bypass run quotas, rate limits or access controls.",
      "Do not copy, reverse-engineer or attempt to extract the source of the service.",
      "Do not upload or generate unlawful, infringing or malicious content.",
      "Do not interfere with the service or with other customers' use of it.",
    ],
  },
  {
    heading: "Your content",
    body: [
      "You keep ownership of your projects, tests, reports and everything else you put into the service. You grant us the permission we need to store and process that content in order to operate the service for you, including sending it to the AI providers described in the Privacy Policy.",
      "You confirm that you have the right to submit the content and credentials you supply.",
    ],
  },
  {
    heading: "AI-generated results",
    body: [
      "Test plans, run results and bug reports are produced with the help of AI and can be incomplete or wrong. They are there to assist your QA process, not to replace human judgement.",
      "A passing run is not proof that your software is free of defects, and you should not rely on it as such.",
    ],
  },
  {
    heading: "Availability and changes to the service",
    body: [
      "We work to keep the service available, but we do not guarantee uninterrupted or error-free operation. Maintenance, upstream provider outages and other events can interrupt it.",
      "Features may be added, changed or removed. Anything labelled beta or preview may change or be withdrawn without notice.",
    ],
  },
  {
    heading: "Suspension and termination",
    body: [
      "We may suspend or close an account that breaches these terms, is used unlawfully, poses a security risk, or where payment fails. Where it is reasonable to do so, we will warn you first.",
      "You can close your account at any time. What happens to your data afterwards is described in the Privacy Policy.",
    ],
  },
  {
    heading: "Disclaimers and liability",
    body: [
      "To the extent the law allows, the service is provided as is and without warranties of any kind, whether express or implied.",
      "To the maximum extent permitted by law, we are not liable for indirect or consequential loss, loss of profits, loss of business, or loss or corruption of data arising from your use of the service.",
      "Nothing in these terms limits any liability that cannot lawfully be limited.",
    ],
  },
  {
    heading: "Changes to these terms",
    body: [
      "If we change these terms we will update this page and the date shown at the top. Continuing to use the service after a change means you accept the updated terms.",
    ],
  },
  ...(ENTITY.jurisdiction
    ? [
        {
          heading: "Governing law",
          body: [
            `These terms are governed by the laws of ${ENTITY.jurisdiction}, and disputes are subject to the exclusive jurisdiction of its courts.`,
          ],
        },
      ]
    : []),
  contactSection("these terms"),
];

const DOCS = {
  privacy: {
    icon: ShieldCheck,
    title: "Privacy Policy",
    intro: "How we collect, use, and protect your information.",
    sections: PRIVACY_SECTIONS,
    other: { to: "/terms", label: "Read our Terms of Service" },
  },
  terms: {
    icon: FileText,
    title: "Terms of Service",
    intro: "The terms that govern your use of AI-QA-Engine.",
    sections: TERMS_SECTIONS,
    other: { to: "/privacy", label: "Read our Privacy Policy" },
  },
};

export default function Legal({ kind = "privacy" }) {
  const doc = DOCS[kind] || DOCS.privacy;
  const Icon = doc.icon;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [kind]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-ink-950">
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl dark:border-white/10 dark:bg-ink-950/80">
        <Container className="flex h-16 items-center justify-between">
          <Link to="/" aria-label="AI-QA-Engine home">
            <Logo />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-300"
            >
              <ArrowLeft size={16} /> Back to home
            </Link>
          </div>
        </Container>
      </header>

      <main>
        <Container className="max-w-3xl py-16">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500/15 text-brand-600 dark:text-brand-300">
            <Icon size={24} />
          </span>
          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            {doc.title}
          </h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300">{doc.intro}</p>
          <p className="mt-2 text-sm text-slate-400 dark:text-slate-500">
            Last updated {LAST_UPDATED}
          </p>

          <nav
            aria-label={`Sections of the ${doc.title}`}
            className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/5"
          >
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              On this page
            </h2>
            <ol className="mt-3 grid gap-2 sm:grid-cols-2">
              {doc.sections.map((section, i) => (
                <li key={section.heading}>
                  <a
                    href={`#${slug(section.heading)}`}
                    className="text-sm text-slate-500 transition-colors hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-300"
                  >
                    {i + 1}. {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-12 space-y-10">
            {doc.sections.map((section, i) => (
              <section
                key={section.heading}
                id={slug(section.heading)}
                className="scroll-mt-24"
              >
                <h2 className="font-display text-xl font-semibold text-slate-900 dark:text-white">
                  {i + 1}. {section.heading}
                </h2>

                {section.body?.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                  >
                    {paragraph}
                  </p>
                ))}

                {section.list && (
                  <ul className="mt-4 space-y-2.5">
                    {section.list.map((item) => (
                      <li
                        key={item}
                        className="flex gap-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                      >
                        <span
                          aria-hidden="true"
                          className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.outro?.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-14 border-t border-slate-200 pt-6 dark:border-white/10">
            <Link
              to={doc.other.to}
              className="text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-300"
            >
              {doc.other.label} →
            </Link>
          </div>
        </Container>
      </main>

      <Footer />
      <Assistant mode="public" />
    </div>
  );
}
